import React from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  IconButton,
  Grid,
  MenuItem,
  Paper,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";

function Closureformbuild({
  closureData,
  handleClosureChange,
  questions,
  addQuestion,
  removeQuestion,
  handleQuestionChange,
  addOption,
  removeOption,
  handleOptionChange,
  toggleNestedQuestion,
  handleNestedQuestionChange,
  addNestedOption,
  removeNestedOption,
  handleNestedOptionChange,
  handleModalClose,
  handleSaveClosureForm,
  submitLoading,
}) {
  const compactInputStyle = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "6px",
      bgcolor: "#fff",
      fontSize: "0.875rem",
    },
    "& .MuiInputLabel-root": { fontSize: "0.875rem" },
  };

  const questionTypes = [
    { value: 1, label: "Radio (Single Choice)" },
    { value: 2, label: "Datepicker" },
    { value: 3, label: "Text/Input Answer" },
    { value: 4, label: "Checkbox (Multiple Choice)" },
  ];
  const hasOptions = (type) => Number(type) !== 2 && Number(type) !== 3;

  // const shiftOptions = [
  //   { value: 1, label: "1 - One time Closure (Any time)" },
  //   { value: 2, label: "2 - Morning & Afternoon (2 times)" },
  //   { value: 3, label: "3 - Morning, Afternoon & Evening (3 times)" },
  // ];
  const shiftOptions = [
    { value: 1, label: "1 Time Closure" },
    { value: 2, label: "2 Time Closure" },
    { value: 3, label: "3 Time Closure" },
  ];

  // ==========================================
  // API PAYLOAD FORMATTER
  // ==========================================
  const prepareSubmit = (e) => {
    e.preventDefault();

    const formattedQuestions = questions.map((q) => {
      let finalOptions;

      if (!hasOptions(q.type)) {
        finalOptions = [];
      } else {
        const hasNested = q.type_options.some(
          (opt) => opt.nestedQuestion && opt.nestedQuestion.Question,
        );

        if (hasNested) {
          finalOptions = {};
          q.type_options.forEach((opt) => {
            if (opt.nestedQuestion && opt.nestedQuestion.Question) {
              finalOptions[opt.value] = {
                Question: opt.nestedQuestion.Question,
                type: opt.nestedQuestion.type,
                type_options:
                  !hasOptions(opt.nestedQuestion.type)
                    ? []
                    : (opt.nestedQuestion.type_options || []).map(
                      (no) => no.value,
                    ),
              };
            } else {
              finalOptions[opt.value] = null;
            }
          });
        } else {
          finalOptions = q.type_options.map((opt) => opt.value);
        }
      }

      return {
        Question: q.Question,
        type: q.type,
        type_options: finalOptions,
      };
    });

    const payload = {
      formName: closureData.formName,
      shift: closureData.shift,
      QuestionForm: formattedQuestions,
    };

    handleSaveClosureForm(payload);
  };

  // ==========================================
  // VALIDATIONS LOGIC (Key / Remark Removed)
  // ==========================================
  const isFormNameEmpty =
    !closureData.formName || closureData.formName.trim() === "";

  let hasQuestionErrors = false;

  questions.forEach((q) => {
    // Sirf Question ke empty hone ka check
    if (!q.Question?.trim()) hasQuestionErrors = true;

    if (hasOptions(q.type)) {
      if (!q.type_options || q.type_options.length < 2)
        hasQuestionErrors = true;

      const trimmedOptions = q.type_options.map((opt) =>
        (opt.value || "").trim().toLowerCase(),
      );

      q.type_options.forEach((opt, optIndex) => {
        if (!opt.value?.trim()) hasQuestionErrors = true;

        // Duplicate option check
        if (
          trimmedOptions.indexOf((opt.value || "").trim().toLowerCase()) !==
          optIndex
        )
          hasQuestionErrors = true;

        // Nested validation
        if (opt.nestedQuestion) {
          if (!opt.nestedQuestion.Question?.trim()) hasQuestionErrors = true;
          if (
            hasOptions(opt.nestedQuestion.type) &&
            (!opt.nestedQuestion.type_options ||
              opt.nestedQuestion.type_options.length < 2)
          ) {
            hasQuestionErrors = true;
          }
          if (hasOptions(opt.nestedQuestion.type)) {
            opt.nestedQuestion.type_options.forEach((no) => {
              if (!no.value?.trim()) hasQuestionErrors = true;
            });
          }
        }
      });
    }
  });

  const isFormInvalid =
    isFormNameEmpty || hasQuestionErrors || questions.length === 0;

  return (
    <form onSubmit={prepareSubmit}>
      <Box sx={{ p: 1 }}>
        {/* --- FORM HEADER DETAILS --- */}
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" alignItems="center" gap={1} mb={2}>
            <AssignmentOutlinedIcon sx={{ color: "#388E3C" }} />
            <Typography variant="h6" fontWeight={600} color="#1e293b">
              Create Closure Form
            </Typography>
          </Stack>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="Form Name"
                value={closureData.formName}
                onChange={handleClosureChange}
                name="formName"
                required
                size="small"
                sx={compactInputStyle}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                select
                fullWidth
                label="Multiple Closures in a Day"
                value={closureData.shift || ""}
                onChange={handleClosureChange}
                name="shift"
                required
                size="small"
                sx={compactInputStyle}
              >
                {shiftOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </Box>

        {/* --- QUESTIONS BUILDER SECTION --- */}
        <Box>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="subtitle1" fontWeight={600} color="#1e293b">
              Form Questions
            </Typography>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={addQuestion}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                color: "#388E3C",
                bgcolor: "rgba(56, 142, 60, 0.1)",
              }}
            >
              Add Question
            </Button>
          </Stack>

          <Stack
            spacing={2}
            sx={{ maxHeight: "50vh", overflowY: "auto", pr: 1 }}
          >
            {questions.map((q, qIndex) => {
              const optionsCountError =
                hasOptions(q.type) && q.type_options.length < 2;

              return (
                <Paper
                  key={qIndex}
                  elevation={0}
                  sx={{
                    p: 2,
                    bgcolor: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                  }}
                >
                  <Grid container spacing={2} alignItems="flex-start">
                    {/* Main Question Fields - Adjusted Grid Layout */}
                    <Grid item xs={12} sm={7}>
                      <TextField
                        fullWidth
                        label={`Question ${qIndex + 1}`}
                        value={q.Question}
                        onChange={(e) =>
                          handleQuestionChange(
                            qIndex,
                            "Question",
                            e.target.value,
                          )
                        }
                        required
                        size="small"
                        sx={compactInputStyle}
                      />
                    </Grid>
                    <Grid item xs={10} sm={4}>
                      <TextField
                        select
                        fullWidth
                        label="Answer Type"
                        value={q.type}
                        onChange={(e) =>
                          handleQuestionChange(qIndex, "type", e.target.value)
                        }
                        required
                        size="small"
                        sx={compactInputStyle}
                      >
                        {questionTypes.map((t) => (
                          <MenuItem key={t.value} value={t.value}>
                            {t.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    <Grid item xs={2} sm={1} sx={{ textAlign: "right" }}>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removeQuestion(qIndex)}
                        sx={{ bgcolor: "rgba(239, 68, 68, 0.1)" }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Grid>

                    {/* Options Builder */}
                    {hasOptions(q.type) && (
                      <Grid item xs={12}>
                        <Box
                          sx={{
                            ml: 2,
                            p: 1.5,
                            borderLeft: "2px solid",
                            borderColor: optionsCountError
                              ? "error.main"
                              : "#388E3C",
                            bgcolor: "#fff",
                            borderRadius: "0 8px 8px 0",
                          }}
                        >
                          <Grid container spacing={2}>
                            {q.type_options.map((opt, optIndex) => (
                              <Grid item xs={12} key={optIndex}>
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                >
                                  <TextField
                                    placeholder={`Option ${optIndex + 1}`}
                                    value={opt.value}
                                    onChange={(e) =>
                                      handleOptionChange(
                                        qIndex,
                                        optIndex,
                                        e.target.value,
                                      )
                                    }
                                    required
                                    size="small"
                                    sx={{
                                      width: "250px",
                                      ...compactInputStyle,
                                    }}
                                  />
                                  <Tooltip
                                    title={
                                      opt.nestedQuestion
                                        ? "Remove Sub-Question"
                                        : "Add Sub-Question"
                                    }
                                  >
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={() =>
                                        toggleNestedQuestion(qIndex, optIndex)
                                      }
                                    >
                                      <AccountTreeOutlinedIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  {q.type_options.length > 1 && (
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() =>
                                        removeOption(qIndex, optIndex)
                                      }
                                    >
                                      <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                  )}
                                </Stack>

                                {/* NESTED QUESTION BUILDER */}
                                {opt.nestedQuestion && (
                                  <Box
                                    sx={{
                                      ml: 4,
                                      mt: 1,
                                      p: 1.5,
                                      border: "1px dashed #cbd5e1",
                                      borderRadius: "6px",
                                      bgcolor: "#f1f5f9",
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      fontWeight={600}
                                      color="text.secondary"
                                      mb={1}
                                      display="block"
                                    >
                                      If user selects "
                                      {opt.value || "this option"}", ask:
                                    </Typography>
                                    <Stack direction="row" spacing={2} mb={1}>
                                      <TextField
                                        fullWidth
                                        label="Sub-Question"
                                        value={opt.nestedQuestion.Question}
                                        onChange={(e) =>
                                          handleNestedQuestionChange(
                                            qIndex,
                                            optIndex,
                                            "Question",
                                            e.target.value,
                                          )
                                        }
                                        size="small"
                                        required
                                        sx={compactInputStyle}
                                      />
                                      <TextField
                                        select
                                        label="Type"
                                        value={opt.nestedQuestion.type}
                                        onChange={(e) =>
                                          handleNestedQuestionChange(
                                            qIndex,
                                            optIndex,
                                            "type",
                                            e.target.value,
                                          )
                                        }
                                        size="small"
                                        sx={{
                                          width: "200px",
                                          ...compactInputStyle,
                                        }}
                                      >
                                        {questionTypes.map((t) => (
                                          <MenuItem
                                            key={t.value}
                                            value={t.value}
                                          >
                                            {t.label}
                                          </MenuItem>
                                        ))}
                                      </TextField>
                                    </Stack>

                                    {/* Nested Options */}
                                    {hasOptions(opt.nestedQuestion.type) && (
                                      <Stack spacing={1}>
                                        {opt.nestedQuestion.type_options.map(
                                          (nOpt, nOptIndex) => (
                                            <Stack
                                              direction="row"
                                              spacing={1}
                                              key={nOptIndex}
                                            >
                                              <TextField
                                                placeholder={`Nested Option ${nOptIndex + 1}`}
                                                value={nOpt.value}
                                                onChange={(e) =>
                                                  handleNestedOptionChange(
                                                    qIndex,
                                                    optIndex,
                                                    nOptIndex,
                                                    e.target.value,
                                                  )
                                                }
                                                size="small"
                                                required
                                                sx={{
                                                  width: "200px",
                                                  ...compactInputStyle,
                                                }}
                                              />
                                              <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() =>
                                                  removeNestedOption(
                                                    qIndex,
                                                    optIndex,
                                                    nOptIndex,
                                                  )
                                                }
                                              >
                                                <DeleteOutlineIcon fontSize="small" />
                                              </IconButton>
                                            </Stack>
                                          ),
                                        )}
                                        <Button
                                          size="small"
                                          onClick={() =>
                                            addNestedOption(qIndex, optIndex)
                                          }
                                          sx={{
                                            textTransform: "none",
                                            alignSelf: "flex-start",
                                          }}
                                        >
                                          + Add Nested Option
                                        </Button>
                                      </Stack>
                                    )}
                                  </Box>
                                )}
                              </Grid>
                            ))}
                            <Grid item xs={12}>
                              <Button
                                size="small"
                                onClick={() => addOption(qIndex)}
                                sx={{ textTransform: "none" }}
                              >
                                + Add Option
                              </Button>
                            </Grid>
                          </Grid>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              );
            })}
          </Stack>
        </Box>

        {/* --- FORM ACTIONS --- */}
        <Stack
          direction="row"
          justifyContent="flex-end"
          spacing={2}
          sx={{ mt: 4 }}
        >
          <Button
            onClick={handleModalClose}
            variant="outlined"
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={submitLoading || isFormInvalid}
            sx={{
              textTransform: "none",
              bgcolor: "#388E3C",
              "&:hover": { bgcolor: "#2E7D32" },
            }}
          >
            {submitLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Save Form"
            )}
          </Button>
        </Stack>
      </Box>
    </form>
  );
}

export default Closureformbuild;
